import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Outlet, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SmoothScroll from "@/components/layout/SmoothScroll";
import FloatingButtons from "@/components/ui/FloatingButtons";

import { Suspense, lazy } from "react";

// Pages
import Home from "@/pages/Home";
import AboutUs from "@/pages/AboutUs";
import RestaurantMenu from "@/pages/RestaurantMenu";
import SpaMenu from "@/pages/SpaMenu";
import Accommodation from "@/pages/Accommodation";
import AccommodationDetail from "@/pages/AccommodationDetail";
import Book from "@/pages/Book";
import Contact from "@/pages/Contact";
import Experiences from "@/pages/Experiences";
import Facilities from "@/pages/Facilities";
import Gallery from "@/pages/Gallery";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import NewsCategory from "@/pages/NewsCategory";
import NewsSearch from "@/pages/NewsSearch";
import FAQ from "@/pages/FAQ";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

// Scroll to top helper on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    if ((window as any).lenis && typeof (window as any).lenis.scrollTo === "function") {
      (window as any).lenis.scrollTo(0, { immediate: true });
    }
  }, [pathname]);

  return null;
}

const supportedLangs = ['en', 'id', 'ar', 'zh-CN', 'nl', 'fr', 'de', 'it', 'pt', 'ru', 'es'];

// Language Handler Component
function LanguageWrapper() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && supportedLangs.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    } else if (!lang && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [lang, i18n]);

  // If URL has a lang parameter but it's not supported, redirect to English version
  if (lang && !supportedLangs.includes(lang)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <FloatingButtons />
    </>
  );
}

function AppContent() {
  return (
    <SmoothScroll>
      <ScrollToTop />
      <Routes>
        {/* Routes with language prefix */}
        <Route path="/:lang" element={<LanguageWrapper />}>
          <Route index element={<Home />} />
          <Route path="about-us" element={<AboutUs />} />
          <Route path="restaurant-menu" element={<RestaurantMenu />} />
          <Route path="spa-menu" element={<SpaMenu />} />
          <Route path="accommodation" element={<Accommodation />} />
          <Route path="accommodation/:slug" element={<AccommodationDetail />} />
          <Route path="book" element={<Book />} />
          <Route path="contact" element={<Contact />} />
          <Route path="experiences" element={<Experiences />} />
          <Route path="facilities" element={<Facilities />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="news" element={<News />} />
          <Route path="news/:slug" element={<NewsDetail />} />
          <Route path="news/category/:categorySlug" element={<NewsCategory />} />
          <Route path="news/search" element={<NewsSearch />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>

        {/* Default English routes (no prefix) */}
        <Route path="/" element={<LanguageWrapper />}>
          <Route index element={<Home />} />
          <Route path="about-us" element={<AboutUs />} />
          <Route path="restaurant-menu" element={<RestaurantMenu />} />
          <Route path="spa-menu" element={<SpaMenu />} />
          <Route path="accommodation" element={<Accommodation />} />
          <Route path="accommodation/:slug" element={<AccommodationDetail />} />
          <Route path="book" element={<Book />} />
          <Route path="contact" element={<Contact />} />
          <Route path="experiences" element={<Experiences />} />
          <Route path="facilities" element={<Facilities />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="news" element={<News />} />
          <Route path="news/:slug" element={<NewsDetail />} />
          <Route path="news/category/:categorySlug" element={<NewsCategory />} />
          <Route path="news/search" element={<NewsSearch />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
        </Route>
      </Routes>
    </SmoothScroll>
  );
}

export default function App() {
  return (
    <Router>
      <div className="min-h-[100dvh] flex flex-col bg-[#0B0B0B] text-[#F5F5F5]">
        <AppContent />
      </div>
    </Router>
  );
}
